import { useEffect } from "react";

import type { RefObject } from "react";

interface UseInfiniteScrollSentinelProps {
  sentinelRef: RefObject<Element>;
  onReachEnd: () => void;
  enabled: boolean;
}

/*
Calls `onReachEnd` whenever `sentinelRef` scrolls into view inside its nearest
scrollable ancestor. Place the sentinel below the last row of a paged list so it
only becomes visible once the user has scrolled to the bottom of what is loaded.

`enabled` must be false while a page is in flight and once the list is
exhausted: the sentinel stays on screen after the observer fires, so leaving it
enabled would request the same next page on every intersection callback.
*/
export function useInfiniteScrollSentinel(
  props: UseInfiniteScrollSentinelProps,
) {
  const { sentinelRef, onReachEnd, enabled } = props;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !enabled) {
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        onReachEnd();
      }
    });

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [sentinelRef, onReachEnd, enabled]);
}
